
import React, { useState } from "react";
import { Terrace, SunStatus, DetailedOpeningHours } from "@/types/terrace";
import { useForm } from "react-hook-form";
import { useAdminTerraces } from "@/hooks/useAdminTerraces";
import TerraceSearchFilter from "./terraces/TerraceSearchFilter";
import TerraceTable from "./terraces/TerraceTable";
import EditTerraceDialog from "./terraces/EditTerraceDialog";

const AdminTerraceManager = () => {
  const { terraces, loading, updateTerrace, deleteTerrace } = useAdminTerraces();
  const [searchTerm, setSearchTerm] = useState("");
  const [editingTerrace, setEditingTerrace] = useState<Terrace | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const form = useForm({
    defaultValues: {
      name: "",
      address: "",
      type: "",
      openingHours: "",
      detailedOpeningHours: {
        monday: { isOpen: false, from: "", to: "" },
        tuesday: { isOpen: false, from: "", to: "" },
        wednesday: { isOpen: false, from: "", to: "" },
        thursday: { isOpen: false, from: "", to: "" },
        friday: { isOpen: false, from: "", to: "" },
        saturday: { isOpen: false, from: "", to: "" },
        sunday: { isOpen: false, from: "", to: "" },
      } as DetailedOpeningHours,
      orientation: "",
      hasFood: false,
      petFriendly: false,
      servesAlcohol: false,
      terraceDescription: "",
      website: "",
      phone: "",
      sunStatus: "not-sunny" as SunStatus,
      googleMapsUrl: "",
      photo_url: "", // Add photo_url to form defaults
    }
  });

  const filteredTerraces = terraces.filter((terrace) => {
    const matchesSearch = terrace.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleEdit = (terrace: Terrace) => {
    setEditingTerrace(terrace);

    // Parse detailed opening hours if available
    let detailedOpeningHours: DetailedOpeningHours = {
      monday: { isOpen: false, from: "", to: "" },
      tuesday: { isOpen: false, from: "", to: "" },
      wednesday: { isOpen: false, from: "", to: "" },
      thursday: { isOpen: false, from: "", to: "" },
      friday: { isOpen: false, from: "", to: "" },
      saturday: { isOpen: false, from: "", to: "" },
      sunday: { isOpen: false, from: "", to: "" },
    };

    if (terrace.detailed_opening_hours) {
      try {
        detailedOpeningHours = typeof terrace.detailed_opening_hours === 'string' 
          ? JSON.parse(terrace.detailed_opening_hours)
          : terrace.detailed_opening_hours;
      } catch (e) {
        console.warn("Failed to parse detailed opening hours:", e);
      }
    }

    // Set form values including photo_url
    form.reset({
      name: terrace.name,
      address: terrace.address,
      type: terrace.type,
      openingHours: terrace.openingHours,
      detailedOpeningHours: detailedOpeningHours,
      orientation: terrace.orientation,
      hasFood: terrace.hasFood,
      petFriendly: terrace.petFriendly,
      servesAlcohol: terrace.servesAlcohol,
      terraceDescription: terrace.terraceDescription || "",
      website: terrace.website || "",
      phone: terrace.phone || "",
      sunStatus: terrace.sunStatus,
      googleMapsUrl: terrace.original_maps_url || "",
      photo_url: terrace.photo_url || "", // Include photo_url in form reset
    });
    setIsDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (editingTerrace) {
      const formValues = form.getValues();
      
      // Map form field names to database column names
      const updatedValues = {
        ...formValues,
        detailed_opening_hours: formValues.detailedOpeningHours, // Map camelCase to snake_case
      };
      
      const success = await updateTerrace(editingTerrace.id, updatedValues);
      if (success) {
        setIsDialogOpen(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-pulse text-lg">Loading terraces...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TerraceSearchFilter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        totalCount={terraces.length}
      />

      <TerraceTable
        terraces={filteredTerraces}
        onEdit={handleEdit}
        onDelete={deleteTerrace}
      />

      <EditTerraceDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        editingTerrace={editingTerrace}
        form={form}
        onSave={handleSaveEdit}
      />
    </div>
  );
};

export default AdminTerraceManager;
